import { Chain } from '@1delta/chain-registry'
import { Lender } from '@1delta/lender-registry'
import { morphoTypeOracles } from '@1delta/data-sdk'
import { ProxyOracleAbi } from '../../../abis/oracle/ProxyOracle'
import {
  MorphoQueryMeta,
  MorphoMarketOverride,
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'
import { formatMorphoPrice } from '../../../utils/parsing'
import { createMarketUid } from '../../../utils'

/**
 * Chains `blue-api.morpho.org` actually indexes, from its own `chains` query.
 *
 * This is an ALLOWLIST on purpose. It used to be a denylist, which meant every
 * chain nobody had thought to exclude was assumed to be indexed — the API then
 * answered `unsupported chainId "<id>"`, the fetcher returned `null`, and
 * because the API path had already been selected the on-chain fallback was
 * skipped by design. The result was SILENT total loss of Morpho oracle prices
 * on Gnosis, Avalanche, Kaia, Pharos, MegaETH, Mantle, Sonic and Linea, while
 * their Morpho lending data kept being fetched.
 *
 * A chain missing from here degrades to the on-chain path, which is correct
 * but slower — the failure mode of a stale allowlist is cost, not absence.
 * Re-check with:
 *   curl -s -X POST https://blue-api.morpho.org/graphql \
 *     -H 'Content-Type: application/json' \
 *     -d '{"query":"query { chains { id network } }"}'
 */
const MORPHO_API_CHAINS = new Set<string>([
  Chain.ETHEREUM_MAINNET,
  Chain.BASE,
  Chain.ARBITRUM_ONE,
  Chain.OP_MAINNET,
  Chain.POLYGON_MAINNET,
  Chain.UNICHAIN,
  Chain.KATANA,
  Chain.HYPEREVM,
  Chain.WORLD_CHAIN,
  Chain.MONAD_MAINNET,
  Chain.STABLE_MAINNET,
  Chain.ROBINHOOD_CHAIN,
  '4217', // Tempo — no chain-registry enum yet
  '5042', // Arc — no chain-registry enum yet
])

/**
 * Check if the Morpho GraphQL API is available for a given chain.
 *
 * Callers use this to classify chains into two strict modes:
 *   - available → API only (ignore overrides)
 *   - unavailable → on-chain from overrides only (skip API)
 */
export function morphoApiAvailable(chainId: string): boolean {
  return MORPHO_API_CHAINS.has(chainId)
}

/**
 * Chains where the remote Morpho price source (official API or Mystic) is
 * known to return incomplete data — typically because no other lender on the
 * chain seeds baseline USD prices, or the source omits the per-market oracle
 * ratio (Mystic). For these chains we run the remote AND the on-chain
 * multicall in parallel and merge: on-chain wins for markets it covers
 * (carries the real oracle ratio), remote fills any markets the on-chain
 * overrides don't list.
 *
 * Add chainIds here as you discover gaps. Default-includes the Mystic-backed
 * chains since their response never carries `state.price`.
 */
const MORPHO_API_INCOMPLETE_CHAINS = new Set<string>([
  Chain.FLARE_MAINNET, // Mystic — no state.price
  Chain.PLUME_MAINNET, // Mystic — no state.price
  Chain.CITREA_MAINNET, // Mystic — no state.price
])

export function morphoApiIncomplete(chainId: string): boolean {
  return MORPHO_API_INCOMPLETE_CHAINS.has(chainId)
}

/**
 * Market overrides for extending Morpho markets beyond JSON data
 */
export type MorphoMarketOverrides = {
  [chainId: string]: MorphoMarketOverride[]
}

/**
 * Generate a unique market ID from oracle, loan asset, and collateral asset
 * Multiple markets can share the same oracle, so we include all three addresses
 * Result is a continuous hex string (oracle 40 chars + loan 40 chars + collateral 40 chars)
 */
function generateMarketId(
  oracle: string,
  loanAsset: string,
  collateralAsset: string,
): string {
  return `${oracle}${loanAsset}${collateralAsset}`
    .replace(/0x/gi, '')
    .toUpperCase()
}

/**
 * Generate a lender key for a Morpho market
 * Format: MORPHO_BLUE_${MARKET_ID}
 */
function generateMorphoLenderKey(marketId: string): string {
  return `${Lender.MORPHO_BLUE}_${marketId}`
}

/**
 * Grouped metadata for deduplicated oracle calls
 * Multiple markets can share the same oracle — one call, many markets
 */
interface MorphoGroupedMeta {
  markets: MorphoQueryMeta[]
}

/**
 * Get Morpho market oracle data for a chain.
 *
 * Callers may pass overrides (markets from a database, carrying real
 * `marketId`s); when they pass none for this chain, the roster falls back to
 * `morphoTypeOracles()` — the same `{oracle, loanAsset, collateralAsset,
 * decimals}` rows, published by `lender-metadata` and already loaded into the
 * data-sdk registry.
 *
 * That fallback is what makes the on-chain path REACHABLE. It used to read the
 * overrides "exclusively", and no production caller passes any — `fetchOracle
 * Prices` takes them as an optional argument that only tests supply — so every
 * chain routed to the on-chain Morpho oracle produced ZERO calls and therefore
 * zero prices, silently. It stayed invisible while the Mystic and blue-api
 * paths answered; the day `api.mysticfinance.xyz` started demanding an API key
 * (401 on every chain, no key anywhere in either repo), Flare / Plume / Citrea
 * had no Morpho price source at all and the "fallback" documented three
 * functions up was a no-op.
 *
 * @param chainId - Chain ID to get markets for
 * @param marketOverrides - Markets from the database with proper market IDs
 */
function getMorphoMarketsForChain(
  chainId: string,
  marketOverrides?: MorphoMarketOverrides,
): MorphoQueryMeta[] {
  const chainOverrides = marketOverrides?.[chainId] ?? []
  const markets = chainOverrides.length
    ? chainOverrides
    : (morphoTypeOracles()?.[chainId]?.[Lender.MORPHO_BLUE] ?? [])

  return markets.map((override) => ({
    oracle: override.oracle,
    loanAsset: override.loanAsset,
    collateralAsset: override.collateralAsset,
    loanAssetDecimals: override.loanAssetDecimals,
    collateralAssetDecimals: override.collateralAssetDecimals,
    fork: Lender.MORPHO_BLUE,
    marketId:
      (override as MorphoMarketOverride).marketId ??
      generateMarketId(
        override.oracle,
        override.loanAsset,
        override.collateralAsset,
      ),
  }))
}

/**
 * Context for Morpho fetcher with optional market overrides
 */
export interface MorphoFetcherContext {
  marketOverrides?: MorphoMarketOverrides
}

/**
 * Get multicall calls for Morpho oracles
 * Groups markets by oracle address to deduplicate price() calls.
 * Each entry in the result still maps back to individual markets via targetLender.
 *
 * @param chainId - Chain ID to get calls for
 * @param context - Optional context with market overrides
 */
export function getMorphoCalls(
  chainId: string,
  context?: MorphoFetcherContext,
): FetcherResult<MorphoGroupedMeta>[] {
  const queries = getMorphoMarketsForChain(chainId, context?.marketOverrides)

  if (queries.length === 0) return []

  // Group markets by oracle address to deduplicate calls
  const oracleGroups = new Map<string, MorphoQueryMeta[]>()
  for (const query of queries) {
    const oracleLc = query.oracle.toLowerCase()
    if (!oracleGroups.has(oracleLc)) {
      oracleGroups.set(oracleLc, [])
    }
    oracleGroups.get(oracleLc)!.push(query)
  }

  // Return one FetcherResult per unique oracle
  return Array.from(oracleGroups.values()).map((markets) => {
    const call: MulticallCall = {
      address: markets[0].oracle,
      name: 'price',
      params: [],
    }

    return {
      calls: [call],
      meta: { markets },
      lender: generateMorphoLenderKey(markets[0].marketId),
    }
  })
}

/**
 * Parse Morpho oracle results into structured price entries
 *
 * Processes all markets sharing the same oracle with a single price() result.
 * Each entry gets a targetLender so it routes to the correct per-market lender key.
 *
 * Morpho oracles return price of collateral in terms of loan asset (debt).
 * To get USD price:
 * - If we know loan asset USD price: collateralUSD = priceDebtToCollateral * loanAssetUSD
 * - If we know collateral USD price: loanAssetUSD = collateralUSD / priceDebtToCollateral
 */
export function parseMorphoResults(
  data: any[],
  meta: MorphoGroupedMeta,
  context: ParseContext,
): OraclePriceEntry[] {
  const { chainId, usdPrices, tokenList } = context
  const entries: OraclePriceEntry[] = []

  const rawPrice = data[0]
  if (!rawPrice || rawPrice === undefined || rawPrice === '0x') return entries

  // Process each market that shares this oracle
  for (const market of meta.markets) {
    const loanAsset = market.loanAsset.toLowerCase()
    const collateralAsset = market.collateralAsset.toLowerCase()

    // Get decimals from query or fall back to token list (default 18)
    const loanAssetDecimals =
      market.loanAssetDecimals ?? tokenList?.[loanAsset]?.decimals ?? 18
    const collateralAssetDecimals =
      market.collateralAssetDecimals ??
      tokenList?.[collateralAsset]?.decimals ??
      18

    const priceDebtToCollateral = formatMorphoPrice(
      rawPrice.toString(),
      loanAssetDecimals,
      collateralAssetDecimals,
    )

    if (isNaN(priceDebtToCollateral) || priceDebtToCollateral === 0) continue

    // Get oracle keys for price lookups (using asset group if available)
    const loanOracleKey =
      tokenList?.[loanAsset]?.assetGroup ?? `${chainId}-${loanAsset}`

    const loanAssetUSD = usdPrices[loanOracleKey] ?? usdPrices[loanAsset]

    const lenderKey = generateMorphoLenderKey(market.marketId)

    if (loanAssetUSD) {
      // The loan asset IS the oracle's unit of account, so its oracle price is
      // always 1.0 (1 loan token = 1 loan token by definition).
      entries.push({
        asset: loanAsset,
        price: 1, // Loan token = 1 in oracle's own denomination
        priceUSD: loanAssetUSD,
        marketUid: createMarketUid(chainId, lenderKey, loanAsset),
        targetLender: lenderKey,
        description: 'Morpho loan asset',
        staticBase: true,
        baseAsset: loanAsset,
      })

      // Add collateral asset entry if we can calculate its USD price
      const collateralPriceUSD = priceDebtToCollateral * loanAssetUSD
      entries.push({
        asset: collateralAsset,
        price: priceDebtToCollateral, // Raw: collateral price in loan asset terms
        priceUSD: collateralPriceUSD,
        marketUid: createMarketUid(chainId, lenderKey, collateralAsset),
        targetLender: lenderKey,
        baseAsset: loanAsset,
      })
    }
  }

  return entries
}

/**
 * Get ABI for Morpho oracle calls
 */
export function getMorphoAbi() {
  return ProxyOracleAbi
}

// ─── GraphQL-based price fetcher ─────────────────────────────────────

const MORPHO_GRAPHQL_URL = 'https://blue-api.morpho.org/graphql'

/**
 * Pages of 200 the price query pulls at most, and only on the `includeUnlisted`
 * branch. With Morpho's own `listed` curation applied no chain exceeds 335
 * markets (Ethereum), so one or two pages exhaust every chain. Unfiltered the
 * indexed book is 7,704 markets — Base alone is 4,280 — so an unbounded loop
 * would turn one price refresh into ~39 sequential round trips.
 */
const MAX_UNLISTED_PRICE_PAGES = 10

const PRICE_PAGE_SIZE = 200

/**
 * `listed` is Morpho's OWN curation flag, and it is the same predicate the
 * market fetcher applies in `lending/public-data/morpho/fetchPublic.ts`. The
 * two MUST agree: a price roster narrower than the market roster renders a
 * correct token amount next to `$0` with a blank LTV in the same row, because
 * `collateralFactor = lltvPrice / collateralPriceUsd` and the divisor is the
 * missing price. It used to be hardcoded here with no way to turn it off,
 * so `includeUnlistedMorphoMarkets` on the market path had no counterpart and
 * enabling it produced unpriceable rows.
 *
 * Ethereum has always run unfiltered on this path; narrowing it now would drop
 * prices that are served today, so its carve-out stays.
 */
export const PRICE_QUERY = (
  first: number,
  skip: number,
  chainId: string,
  includeUnlisted: boolean,
) => `
query GetMarketPrices {
  markets(first: ${first}, skip: ${skip}, where: {
    chainId_in: [${chainId}]${
      !includeUnlisted && chainId != Chain.ETHEREUM_MAINNET
        ? `,listed: true`
        : ''
    }
  },
  orderBy: SupplyAssetsUsd,
  orderDirection: Desc
  ) {
    items {
      uniqueKey: marketId
      oracleAddress
      loanAsset {
        address
        decimals
        priceUsd
      }
      collateralAsset {
        address
        decimals
        priceUsd
      }
      state {
        price
      }
    }
  }
}
`

export interface MorphoGqlMarket {
  uniqueKey: string
  oracleAddress: string
  loanAsset: { address: string; decimals: number; priceUsd: number | null }
  collateralAsset: {
    address: string
    decimals: number
    priceUsd: number | null
  } | null
  state: { price: string | null } | null
}

/**
 * Fetch Morpho oracle prices from the GraphQL API instead of on-chain calls.
 * Returns OraclePriceEntry[] directly, or null if the API is unavailable.
 *
 * This avoids hundreds of on-chain price() calls on chains where the API is available.
 */
export async function fetchMorphoGraphQLPrices(
  chainId: string,
  includeUnlisted = false,
): Promise<OraclePriceEntry[] | null> {
  if (!morphoApiAvailable(chainId)) return null

  const post = (skip: number) =>
    fetch(MORPHO_GRAPHQL_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: PRICE_QUERY(PRICE_PAGE_SIZE, skip, chainId, includeUnlisted),
      }),
    }).then((r) => r.json())

  const readItems = (result: any): MorphoGqlMarket[] | null => {
    const items = result?.data?.markets?.items
    if (!Array.isArray(items)) {
      console.warn(
        `[morpho-gql] chain ${chainId}: unexpected API shape, items is not an array`,
      )
      return null
    }
    return items
  }

  try {
    const allMarkets: MorphoGqlMarket[] = []

    if (includeUnlisted) {
      // The unfiltered book has no useful upper bound per chain, so page until
      // the API returns a short page. Sequential by necessity — the stop
      // condition is the previous page's length.
      for (let page = 0; page < MAX_UNLISTED_PRICE_PAGES; page++) {
        const items = readItems(await post(page * PRICE_PAGE_SIZE))
        if (!items) return null
        allMarkets.push(...items)
        if (items.length < PRICE_PAGE_SIZE) break
        if (page === MAX_UNLISTED_PRICE_PAGES - 1) {
          console.warn(
            `[morpho-gql] chain ${chainId}: hit MAX_UNLISTED_PRICE_PAGES ` +
              `(${MAX_UNLISTED_PRICE_PAGES * PRICE_PAGE_SIZE} markets); ` +
              `markets below that rank by supply are unpriced`,
          )
        }
      }
    } else {
      // Listed-only: Ethereum needs two pages (335 markets), every other chain
      // fits in one. Fixed count, so both pages go out in parallel — this is
      // the hot path and it must not gain a round trip.
      const skips =
        chainId === Chain.ETHEREUM_MAINNET ? [0, PRICE_PAGE_SIZE] : [0]
      const results = await Promise.all(skips.map(post))
      for (const result of results) {
        const items = readItems(result)
        if (!items) return null
        allMarkets.push(...items)
      }
    }

    return processMarketsToEntries(chainId, allMarkets, 'morpho-gql')
  } catch (err) {
    console.warn(
      `[morpho-gql] chain ${chainId}: API error, falling back to on-chain`,
      err,
    )
    return null
  }
}

// ─── Shared market → entry conversion ───────────────────────────────

/**
 * Convert MorphoGqlMarket[] to OraclePriceEntry[].
 * Shared by the Morpho GraphQL and Mystic paths.
 */
export function processMarketsToEntries(
  chainId: string,
  allMarkets: MorphoGqlMarket[],
  source: string,
): OraclePriceEntry[] | null {
  if (allMarkets.length === 0) {
    console.warn(`[${source}] chain ${chainId}: returned 0 markets`)
    return null
  }

  const entries: OraclePriceEntry[] = []

  for (const market of allMarkets) {
    if (!market.collateralAsset?.address || !market.loanAsset?.address) continue

    const oracle = market.oracleAddress
    const loanAsset = market.loanAsset.address.toLowerCase()
    const collateralAsset = market.collateralAsset.address.toLowerCase()
    const marketId =
      market.uniqueKey?.replace(/^0x/i, '').toUpperCase() ??
      generateMarketId(oracle, loanAsset, collateralAsset)

    const lenderKey = generateMorphoLenderKey(marketId)

    // Extract USD prices from the API
    const loanPriceUSD = market.loanAsset.priceUsd
    const collateralPriceUSD = market.collateralAsset.priceUsd

    // Get the oracle ratio from state.price
    const statePrice = market.state?.price
    const oracleRatio =
      statePrice != null
        ? formatMorphoPrice(
            statePrice,
            market.loanAsset.decimals,
            market.collateralAsset.decimals,
          )
        : 0

    // Prefer direct API prices for each side; only derive from ratio when
    // the direct price is missing. This mirrors the on-chain path where the
    // loan asset always uses its known USD price directly.

    // Resolve USD price for each side:
    // - Direct API price takes precedence
    // - Derived from ratio + other side as fallback
    const resolvedLoanUSD =
      loanPriceUSD != null && loanPriceUSD > 0
        ? loanPriceUSD
        : collateralPriceUSD != null &&
            collateralPriceUSD > 0 &&
            oracleRatio > 0
          ? collateralPriceUSD / oracleRatio
          : 0

    const resolvedCollateralUSD =
      collateralPriceUSD != null && collateralPriceUSD > 0
        ? collateralPriceUSD
        : loanPriceUSD != null && loanPriceUSD > 0 && oracleRatio > 0
          ? oracleRatio * loanPriceUSD
          : 0

    // Classify per side, exactly as the number above was obtained: the API's
    // own `priceUsd` is a USD price for that asset; the fallback branch
    // multiplies the other side by the oracle ratio and inherits its error.
    const collateralFromApi =
      collateralPriceUSD != null && collateralPriceUSD > 0
    const loanFromApi = loanPriceUSD != null && loanPriceUSD > 0

    // Add collateral entry
    if (resolvedCollateralUSD > 0) {
      entries.push({
        asset: collateralAsset,
        price: oracleRatio, // Raw: collateral price in loan asset terms
        priceUSD: resolvedCollateralUSD,
        marketUid: createMarketUid(chainId, lenderKey, collateralAsset),
        targetLender: lenderKey,
        baseAsset: loanAsset,
        derivation: collateralFromApi ? 'direct' : 'derived',
      })
    }

    // Add loan entry
    if (resolvedLoanUSD > 0) {
      entries.push({
        asset: loanAsset,
        price: oracleRatio > 0 ? 1 / oracleRatio : 0,
        priceUSD: resolvedLoanUSD,
        marketUid: createMarketUid(chainId, lenderKey, loanAsset),
        targetLender: lenderKey,
        description: 'Morpho loan asset',
        staticBase: true,
        baseAsset: loanAsset,
        derivation: loanFromApi ? 'direct' : 'derived',
      })
    }
  }

  if (entries.length === 0) {
    console.warn(
      `[${source}] chain ${chainId}: ${allMarkets.length} markets fetched but 0 price entries produced`,
    )
    return null
  }
  return entries
}

/**
 * Morpho Oracle Fetcher module
 */
export const morphoFetcher = {
  getCalls: getMorphoCalls,
  parse: parseMorphoResults,
  getAbi: getMorphoAbi,
}
